import {
  useCallback, useMemo,
} from 'react';
import { useDispatch } from 'react-redux';
import { Shape } from '~/_shared/baseComponents/numberInShape/numberInShape.component';
import { PRESET_COLORS_PRIMARY } from '~/_shared/constants/colors.constants';
import { ProximityType } from '~/_shared/types/proximity/proximity.enums';
import { isGroupRadius } from '~/_shared/types/proximity/proximity.types';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { areSpreadsheetRowIdsEqual } from '~/_shared/utils/spreadsheet/generalSpreadsheet.helpers';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useProximityLocation } from '~/map/map/proximity/useProximityLocation';
import { getProximitiesDetails } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.helpers';
import type { OverlappingMapElement } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.types';
import { getGroupRadiusProximityDetails } from '~/proximity/proximity.helpers';
import { useProximities } from '~/proximity/useProximities';
import {
  activeMapElementsSetActiveProximity, proximityHovered, proximityHoverEnded,
} from '~/store/frontendState/activeMapElements/activeMapElements.actionCreators';
import {
  useActiveOverlappingElementsSelector, useActiveProximitySelector,
} from '~/store/frontendState/activeMapElements/activeMapElements.selectors';
import {
  ActiveMapElementsConfiguration, type OverlappingProximity,
} from '~/store/frontendState/activeMapElements/activeMapElements.state';

export const useOverlappingElementsController = () => {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const activeOverlappingElements = useActiveOverlappingElementsSelector();
  const activeProximity = useActiveProximitySelector();
  const proximities = useProximities();
  const { getGroupRadiusProximityLocations } = useProximityLocation();

  const proximityDetails = useMemo(() =>
    getProximitiesDetails(proximities, getGroupRadiusProximityLocations),
  [proximities, getGroupRadiusProximityLocations]);

  const translateProximities = useCallback((
    activeOverlappingProximities: ReadonlyArray<OverlappingProximity>
  ): ReadonlyArray<OverlappingMapElement> => (
    activeOverlappingProximities
      .map(activeOverlap => {
        const { proximityId, spreadsheetRowId } = activeOverlap;
        const details = proximityDetails.get(proximityId);
        if (!details) {
          return getUnknownElement(t); // Keep the element count and order unchanged—subsequent code depends on their indices
        }

        const { proximity, index, groupCircleIndexesMap } = details;

        const spreadsheetIdsEqual = (!activeProximity?.spreadsheetRowId && !spreadsheetRowId) ||
          areSpreadsheetRowIdsEqual(activeProximity?.spreadsheetRowId, spreadsheetRowId);

        let name = proximity.name;
        let color = proximity.styles.color;
        if (isGroupRadius(proximity) && groupCircleIndexesMap && spreadsheetRowId) {
          const groupIndex = groupCircleIndexesMap.get(spreadsheetRowId);

          if (notNullsy(groupIndex)) {
            name = `${name} | ${groupIndex + 1}`;
          }

          const stylesOverride = getGroupRadiusProximityDetails(proximity, spreadsheetRowId).styles;
          if (stylesOverride) {
            color = stylesOverride.color;
          }
        }

        return {
          shape: proximity.type === ProximityType.DriveTimePolygon ? Shape.square : Shape.circle,
          active: proximityId === activeProximity?.proximityId && spreadsheetIdsEqual,
          backgroundColor: color,
          label: index + 1,
          name,
          onClick: () => {
            dispatch(activeMapElementsSetActiveProximity(proximityId, spreadsheetRowId));
          },
          onHoverStart: () => {
            dispatch(proximityHovered(proximityId, spreadsheetRowId));
          },
          onHoverEnd: () => {
            dispatch(proximityHoverEnded(proximityId, spreadsheetRowId));
          },
        };
      })),
  [activeProximity?.proximityId, activeProximity?.spreadsheetRowId, dispatch, proximityDetails, t]);

  const translatedActiveOverlappingElements = useMemo(() => {
    if (activeOverlappingElements.configuration === ActiveMapElementsConfiguration.Proximity) {
      return translateProximities(activeOverlappingElements.proximities);
    }

    return [];
  }, [activeOverlappingElements.proximities, activeOverlappingElements.configuration, translateProximities]);

  return {
    overlappingElements: translatedActiveOverlappingElements,
  };
};

const getUnknownElement = (t: TranslationFnc): OverlappingMapElement => ({
  shape: Shape.circle,
  active: false,
  backgroundColor: PRESET_COLORS_PRIMARY[0],
  label: -1,
  name: t('Unknown'),
  onClick: () => null,
});
